import { addDays, format, isSameDay, startOfWeek } from 'date-fns'
import { Check, Pencil, Plus, Settings, Sparkles, StickyNote, Trash2, Users, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { WeekNavigator } from '@/components/shared/WeekNavigator'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useDailyNotesCreateOrUpdate, useWeeklyNotes, useWeeklyNotesCreateOrUpdate } from '@/hooks'
import { ApiError } from '@/libs/api'
import { APP_ROUTES, CONSTANTS } from '@/libs/constants'
import type {
  DailyNote,
  DailyNoteInput,
  DailyNotePayload,
  WeeklyNotePayload,
} from '@/libs/interfaces'
import { cn } from '@/libs/utils'
import { useAppSelector } from '@/store/hooks'

interface WeeklyNotesProps {
  editable?: boolean
  showManageLink?: boolean
}

interface DailyNoteMutationOptions {
  dateKey: string
  noteText: string
  successMessage: string
  errorMessage: string
}

function getTrimmedStringProperty(value: object, key: string): string | null {
  if (!(key in value)) return null

  const propertyValue = (value as Record<string, unknown>)[key]
  return typeof propertyValue === 'string' && propertyValue.trim() ? propertyValue.trim() : null
}

function getNestedErrorMessage(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }

  if (value && typeof value === 'object') {
    return getTrimmedStringProperty(value, 'message') ?? getTrimmedStringProperty(value, 'error')
  }

  return null
}

function getMutationErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return getNestedErrorMessage(error.data) ?? error.message ?? fallback
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message.trim()
  }

  return getNestedErrorMessage(error) ?? fallback
}

function buildLocalDailyNote({
  existingNote,
  storeID,
  dateKey,
  noteText,
}: {
  existingNote?: DailyNote
  storeID: string
  dateKey: string
  noteText: string
}): DailyNote {
  const timestamp = new Date().toISOString()

  return {
    storeDailyNoteID: existingNote?.storeDailyNoteID ?? `local-${dateKey}`,
    storeID,
    date: dateKey,
    note: noteText,
    createdAt: existingNote?.createdAt ?? timestamp,
    updatedAt: timestamp,
  }
}

function replaceDailyNotesForDate(
  currentNotes: DailyNote[],
  dateKey: string,
  nextNote?: DailyNote,
) {
  const filteredNotes = currentNotes.filter((note) => note.date !== dateKey)
  return nextNote ? [...filteredNotes, nextNote] : filteredNotes
}

interface WeeklySummarySectionProps {
  editable: boolean
  editingWeekly: boolean
  weeklySummary: string
  weeklyEditText: string
  isWeeklySaving: boolean
  onStartEdit: () => void
  onCancelEdit: () => void
  onSave: () => void
  onChangeText: (value: string) => void
}

function WeeklySummarySection({
  editable,
  editingWeekly,
  weeklySummary,
  weeklyEditText,
  isWeeklySaving,
  onStartEdit,
  onCancelEdit,
  onSave,
  onChangeText,
}: WeeklySummarySectionProps) {
  const { t } = useTranslation()
  const isEditing = editable && editingWeekly

  return (
    <div
      className={cn(
        'relative border border-primary/20 shadow-sm',
        editable
          ? 'rounded-lg bg-primary/5 p-4'
          : 'rounded-xl bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-4',
      )}
    >
      {!editable ? (
        <div className="absolute right-3 top-3">
          <Sparkles className="h-4 w-4 text-primary/40" />
        </div>
      ) : null}

      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="row">
          <StickyNote className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold text-primary">{t('dashboard.weeklySummary')}</span>
        </div>

        {editable && !editingWeekly ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-primary/70 hover:text-primary"
            disabled={isWeeklySaving}
            onClick={onStartEdit}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        ) : null}
      </div>

      {isEditing ? (
        <div className="space-y-2">
          <Textarea
            value={weeklyEditText}
            onChange={(event) => onChangeText(event.target.value)}
            placeholder={t('dashboard.weeklySummaryPlaceholder')}
            className="min-h-[80px] resize-none bg-background text-sm"
            autoFocus
            disabled={isWeeklySaving}
          />
          <div className="flex gap-2">
            <Button size="sm" disabled={isWeeklySaving} onClick={onSave}>
              <Check className="mr-1 h-4 w-4" />
              {isWeeklySaving ? t('common.saving') : t('common.save')}
            </Button>
            <Button size="sm" variant="ghost" disabled={isWeeklySaving} onClick={onCancelEdit}>
              <X className="mr-1 h-4 w-4" />
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <p className={cn('text-sm leading-relaxed text-foreground', editable ? '' : 'pr-6')}>
          {weeklySummary || (
            <span className="italic text-muted-foreground">{t('dashboard.noNotes')}</span>
          )}
        </p>
      )}
    </div>
  )
}

function getDayNameClassName(todayFlag: boolean): string {
  return cn('text-sm font-semibold', todayFlag ? 'text-primary' : 'text-foreground')
}

function getDayDateClassName(editable: boolean, todayFlag: boolean): string {
  const base = editable ? 'text-xs' : 'rounded px-1.5 py-0.5 text-xs font-medium'

  if (!todayFlag) return cn(base, 'text-muted-foreground')

  return cn(base, editable ? 'text-primary/70' : 'bg-primary text-primary-foreground')
}

interface TodayBadgeProps {
  editable: boolean
}

function TodayBadge({ editable }: TodayBadgeProps) {
  const { t } = useTranslation()

  if (editable) {
    return (
      <span className="mt-1 w-fit rounded bg-primary px-1.5 py-0.5 text-xs text-primary-foreground">
        {t('dashboard.todayBadge')}
      </span>
    )
  }

  return (
    <Badge
      variant="default"
      className="mt-1.5 h-4 bg-primary/90 px-1.5 py-0 text-[10px] hover:bg-primary"
    >
      {t('dashboard.todayBadge')}
    </Badge>
  )
}

interface WeeklyNoteDayLabelProps {
  day: Date
  editable: boolean
  todayFlag: boolean
}

function WeeklyNoteDayLabel({ day, editable, todayFlag }: WeeklyNoteDayLabelProps) {
  return (
    <div className={cn('flex-shrink-0', editable ? 'w-28' : 'w-[72px]')}>
      <div className={cn('flex', editable ? 'flex-col' : 'items-center gap-1.5')}>
        <span className={getDayNameClassName(todayFlag)}>
          {format(day, editable ? 'EEEE' : 'EEE')}
        </span>
        <span className={getDayDateClassName(editable, todayFlag)}>
          {format(day, editable ? 'MMM d, yyyy' : 'd')}
        </span>
      </div>

      {todayFlag ? <TodayBadge editable={editable} /> : null}
    </div>
  )
}

interface WeeklyNoteDayContentProps {
  editable: boolean
  todayFlag: boolean
  isEditing: boolean
  isSavingThisDay: boolean
  hasNotes: boolean
  dayNotes: DailyNote[]
  editText: string
  onChangeEditText: (value: string) => void
  onSave: () => void
  onCancel: () => void
}

function WeeklyNoteDayContent({
  editable,
  todayFlag,
  isEditing,
  isSavingThisDay,
  hasNotes,
  dayNotes,
  editText,
  onChangeEditText,
  onSave,
  onCancel,
}: WeeklyNoteDayContentProps) {
  const { t } = useTranslation()

  if (editable && isEditing) {
    return (
      <div className="space-y-2">
        <Textarea
          value={editText}
          onChange={(event) => onChangeEditText(event.target.value)}
          placeholder={t('dashboard.notePlaceholder')}
          className="min-h-[60px] resize-none text-sm"
          autoFocus
          disabled={isSavingThisDay}
        />
        <div className="flex gap-2">
          <Button size="sm" disabled={isSavingThisDay} onClick={onSave}>
            <Check className="mr-1 h-4 w-4" />
            {isSavingThisDay ? t('common.saving') : t('common.save')}
          </Button>
          <Button size="sm" variant="ghost" disabled={isSavingThisDay} onClick={onCancel}>
            <X className="mr-1 h-4 w-4" />
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    )
  }

  if (hasNotes) {
    return (
      <div className="space-y-1">
        {dayNotes.map((note) => (
          <p
            key={note.storeDailyNoteID}
            className={cn(
              'text-sm',
              todayFlag && !editable ? 'font-medium text-foreground' : 'text-card-foreground',
            )}
          >
            {note.note}
          </p>
        ))}
      </div>
    )
  }

  return <p className="text-sm italic text-muted-foreground/50">{t('dashboard.noNotesDay')}</p>
}

interface WeeklyNoteDayActionsProps {
  editable: boolean
  isEditing: boolean
  hasNotes: boolean
  isDailySaving: boolean
  onStartEdit: () => void
  onDelete: () => void
}

function WeeklyNoteDayActions({
  editable,
  isEditing,
  hasNotes,
  isDailySaving,
  onStartEdit,
  onDelete,
}: WeeklyNoteDayActionsProps) {
  if (!editable || isEditing) return null

  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-foreground"
        disabled={isDailySaving}
        onClick={onStartEdit}
      >
        {hasNotes ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
      </Button>
      {hasNotes ? (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          disabled={isDailySaving}
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  )
}

interface WeeklyNoteDayRowProps {
  day: Date
  editable: boolean
  todayFlag: boolean
  hasNotes: boolean
  dayNotes: DailyNote[]
  isEditing: boolean
  isSavingThisDay: boolean
  isDailySaving: boolean
  editText: string
  onChangeEditText: (value: string) => void
  onSave: () => void
  onCancel: () => void
  onStartEdit: () => void
  onDelete: () => void
}

function WeeklyNoteDayRow({
  day,
  editable,
  todayFlag,
  hasNotes,
  dayNotes,
  isEditing,
  isSavingThisDay,
  isDailySaving,
  editText,
  onChangeEditText,
  onSave,
  onCancel,
  onStartEdit,
  onDelete,
}: WeeklyNoteDayRowProps) {
  return (
    <div
      className={cn(
        'rounded-lg border transition-all duration-200',
        editable ? 'p-4' : 'group relative p-3',
        todayFlag
          ? 'border-primary/40 bg-gradient-to-r from-primary/5 to-primary/10 shadow-sm'
          : hasNotes
            ? 'border-border/60 bg-muted/40 hover:border-border hover:bg-muted/60'
            : 'border-border/30 bg-muted/20 hover:bg-muted/30',
      )}
    >
      <div className="flex items-start gap-3">
        <WeeklyNoteDayLabel day={day} editable={editable} todayFlag={todayFlag} />

        <div className="flex-1">
          <WeeklyNoteDayContent
            editable={editable}
            todayFlag={todayFlag}
            isEditing={isEditing}
            isSavingThisDay={isSavingThisDay}
            hasNotes={hasNotes}
            dayNotes={dayNotes}
            editText={editText}
            onChangeEditText={onChangeEditText}
            onSave={onSave}
            onCancel={onCancel}
          />
        </div>

        <WeeklyNoteDayActions
          editable={editable}
          isEditing={isEditing}
          hasNotes={hasNotes}
          isDailySaving={isDailySaving}
          onStartEdit={onStartEdit}
          onDelete={onDelete}
        />
      </div>
    </div>
  )
}

function getWeeklyNotesRootClassName(editable: boolean): string {
  return cn(editable && 'space-y-6')
}

function getWeeklyNotesCardClassName(editable: boolean): string {
  return cn(
    editable
      ? 'border-border bg-card'
      : 'animate-fade-in overflow-hidden border-border/50 bg-gradient-to-br from-card via-card to-primary/[0.02]',
  )
}

function getWeeklyNotesCardContentClassName(editable: boolean): string {
  return cn(editable ? 'space-y-6' : 'space-y-3 p-3')
}

function getMutatingOverlayClassName(isMutating: boolean): string {
  return cn(isMutating && 'pointer-events-none opacity-70')
}

interface OptionalSettingsHeaderProps {
  editable: boolean
}

function OptionalSettingsHeader({ editable }: OptionalSettingsHeaderProps) {
  const { t } = useTranslation()

  if (!editable) return null

  return (
    <SettingsPageHeader
      title={t('settings.weeklyNotes.title')}
      description={t('settings.weeklyNotes.description')}
    />
  )
}

interface WeeklyNotesReadOnlyHeaderProps {
  editable: boolean
  notesCount: number
  showManageLink: boolean
}

function WeeklyNotesReadOnlyHeader({
  editable,
  notesCount,
  showManageLink,
}: WeeklyNotesReadOnlyHeaderProps) {
  const { t } = useTranslation()

  if (editable) return null

  return (
    <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b border-border/30 pb-4 px-3 md:px-4">
      <div className="row">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 md:h-8 md:w-8">
          <Users className="h-3.5 w-3.5 text-primary md:h-4 md:w-4" />
        </div>
        <div>
          <CardTitle className="text-sm font-semibold md:text-base">
            {t('dashboard.weeklyTeamBoard')}
          </CardTitle>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            {notesCount} {t('dashboard.weeklyNotes').toLowerCase()}
          </p>
        </div>
      </div>
      {showManageLink ? (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 hover:bg-muted"
          asChild
          title={t('dashboard.manageWeeklyNotes')}
        >
          <Link to={APP_ROUTES.settings.weeklyNotes}>
            <Settings className="h-4 w-4" />
          </Link>
        </Button>
      ) : null}
    </CardHeader>
  )
}

interface OptionalDailyNotesLabelProps {
  editable: boolean
}

function OptionalDailyNotesLabel({ editable }: OptionalDailyNotesLabelProps) {
  const { t } = useTranslation()

  if (!editable) return null

  return <Label className="text-base font-medium">{t('settings.weeklyNotes.dailyNotes')}</Label>
}

export function WeeklyNotes({ editable = false, showManageLink = false }: WeeklyNotesProps) {
  const { t } = useTranslation()
  const storeID = useAppSelector((state) => state.auth?.currentStore?.storeID) ?? ''
  const [currentWeekStart, setCurrentWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 }),
  )
  const [weeklySummary, setWeeklySummary] = useState('')
  const [dailyNotes, setDailyNotes] = useState<DailyNote[]>([])
  const [editingWeekly, setEditingWeekly] = useState(false)
  const [weeklyEditText, setWeeklyEditText] = useState('')
  const [editingDay, setEditingDay] = useState<string | null>(null)
  const [editText, setEditText] = useState('')

  const weekKey = format(currentWeekStart, CONSTANTS.DATE_FORMAT)
  const { data: weeklyNotesData } = useWeeklyNotes({
    params: { storeID, week: weekKey },
    enabled: !!storeID,
  })

  const createOrUpdateWeeklyNotes = useWeeklyNotesCreateOrUpdate()
  const createOrUpdateDailyNotes = useDailyNotesCreateOrUpdate()
  const isWeeklySaving = createOrUpdateWeeklyNotes.isPending
  const isDailySaving = createOrUpdateDailyNotes.isPending
  const isMutating = isWeeklySaving || isDailySaving

  useEffect(() => {
    setWeeklySummary(weeklyNotesData?.weekNote?.note ?? '')
    setDailyNotes(weeklyNotesData?.dailyNotes ?? [])
    setEditingWeekly(false)
    setWeeklyEditText('')
    setEditingDay(null)
    setEditText('')
  }, [weeklyNotesData])

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(currentWeekStart, index)),
    [currentWeekStart],
  )

  const notesByDate = useMemo(() => {
    const groupedNotes = new Map<string, DailyNote[]>()

    dailyNotes.forEach((note) => {
      const notesForDate = groupedNotes.get(note.date)

      if (notesForDate) {
        notesForDate.push(note)
        return
      }

      groupedNotes.set(note.date, [note])
    })

    return groupedNotes
  }, [dailyNotes])

  const notesCount = dailyNotes.length

  const getNotesForDate = (dateKey: string) => notesByDate.get(dateKey) ?? []

  const getNoteTextForDate = (dateKey: string) =>
    getNotesForDate(dateKey)
      .map((note) => note.note)
      .join('\n')

  const isToday = (date: Date) => isSameDay(date, new Date())

  const handleStartWeeklyEdit = () => {
    setEditingWeekly(true)
    setWeeklyEditText(weeklySummary)
  }

  const handleCancelWeeklyEdit = () => {
    setWeeklyEditText(weeklySummary)
    setEditingWeekly(false)
  }

  const handleSaveWeeklyNote = async () => {
    if (!storeID) {
      toast.error(t('common.error'))
      return
    }

    const nextSummary = weeklyEditText.trim()

    if (nextSummary === weeklySummary) {
      setWeeklyEditText(nextSummary)
      setEditingWeekly(false)
      return
    }

    try {
      const payload: WeeklyNotePayload = {
        storeID,
        week: weekKey,
        weekNote: nextSummary,
        dailyNotes: [],
      }

      const savedWeeklyNotes = await createOrUpdateWeeklyNotes.mutateAsync(payload)
      const savedSummary = savedWeeklyNotes?.weekNote?.note ?? nextSummary

      setWeeklySummary(savedSummary)
      setEditingWeekly(false)
      setWeeklyEditText(savedSummary)

      if (savedWeeklyNotes?.dailyNotes !== undefined) {
        setDailyNotes(savedWeeklyNotes.dailyNotes)
      }

      toast.success(t('settings.weeklyNotes.summarySaved'))
    } catch (error) {
      toast.error(getMutationErrorMessage(error, t('settings.weeklyNotes.summarySaveFailed')))
    }
  }

  const handleStartEdit = (dateKey: string) => {
    setEditingDay(dateKey)
    setEditText(getNoteTextForDate(dateKey))
  }

  const handleCancelEdit = () => {
    if (editingDay) {
      setEditText(getNoteTextForDate(editingDay))
    }

    setEditingDay(null)
  }

  const persistDailyNote = async ({
    dateKey,
    noteText,
    successMessage,
    errorMessage,
  }: DailyNoteMutationOptions) => {
    if (!storeID) {
      toast.error(t('common.error'))
      return false
    }

    const existingNotes = getNotesForDate(dateKey)
    const existingNote = existingNotes[0]
    const nextDailyNotes: DailyNoteInput[] = [{ date: dateKey, note: noteText }]
    const payload: DailyNotePayload = {
      storeID,
      dailyNotes: nextDailyNotes,
    }

    try {
      const savedDailyNotes = await createOrUpdateDailyNotes.mutateAsync(payload)
      const savedNote = savedDailyNotes?.dailyNotes?.find((note) => note.date === dateKey)
      const nextNote =
        noteText === ''
          ? undefined
          : (savedNote ??
            buildLocalDailyNote({
              existingNote,
              storeID,
              dateKey,
              noteText,
            }))

      setDailyNotes((currentNotes) => replaceDailyNotesForDate(currentNotes, dateKey, nextNote))
      toast.success(successMessage)
      return true
    } catch (error) {
      toast.error(getMutationErrorMessage(error, errorMessage))
      return false
    }
  }

  const handleSaveNote = async () => {
    if (!editingDay) return

    const dateKey = editingDay
    const previousText = getNoteTextForDate(dateKey)
    const nextText = editText.trim()

    if (nextText === previousText) {
      setEditText(nextText)
      setEditingDay(null)
      return
    }

    const didSave = await persistDailyNote({
      dateKey,
      noteText: nextText,
      successMessage: nextText
        ? t('settings.weeklyNotes.noteSaved')
        : t('settings.weeklyNotes.noteDeleted'),
      errorMessage: nextText
        ? t('settings.weeklyNotes.noteSaveFailed')
        : t('settings.weeklyNotes.noteDeleteFailed'),
    })

    if (!didSave) return

    setEditText(nextText)
    setEditingDay(null)
  }

  const handleDeleteNote = async (dateKey: string) => {
    const previousText = getNoteTextForDate(dateKey)

    if (!previousText) return

    const didDelete = await persistDailyNote({
      dateKey,
      noteText: '',
      successMessage: t('settings.weeklyNotes.noteDeleted'),
      errorMessage: t('settings.weeklyNotes.noteDeleteFailed'),
    })

    if (!didDelete) return

    if (editingDay === dateKey) {
      setEditText('')
      setEditingDay(null)
    }
  }

  return (
    <div className={getWeeklyNotesRootClassName(editable)}>
      <OptionalSettingsHeader editable={editable} />
      <Card className={getWeeklyNotesCardClassName(editable)}>
        <WeeklyNotesReadOnlyHeader
          editable={editable}
          notesCount={notesCount}
          showManageLink={showManageLink}
        />

        <CardContent className={getWeeklyNotesCardContentClassName(editable)}>
          <div className={getMutatingOverlayClassName(isMutating)}>
            <WeekNavigator currentWeekStart={currentWeekStart} onWeekChange={setCurrentWeekStart} />
          </div>

          <WeeklySummarySection
            editable={editable}
            editingWeekly={editingWeekly}
            weeklySummary={weeklySummary}
            weeklyEditText={weeklyEditText}
            isWeeklySaving={isWeeklySaving}
            onStartEdit={handleStartWeeklyEdit}
            onCancelEdit={handleCancelWeeklyEdit}
            onSave={() => void handleSaveWeeklyNote()}
            onChangeText={setWeeklyEditText}
          />

          <div className="space-y-3">
            <OptionalDailyNotesLabel editable={editable} />

            <div className="grid gap-2">
              {weekDays.map((day) => {
                const dateKey = format(day, CONSTANTS.DATE_FORMAT)
                const dayNotes = getNotesForDate(dateKey)
                const isEditing = editingDay === dateKey
                const isSavingThisDay = isDailySaving && editingDay === dateKey

                return (
                  <WeeklyNoteDayRow
                    key={dateKey}
                    day={day}
                    editable={editable}
                    todayFlag={isToday(day)}
                    hasNotes={dayNotes.length > 0}
                    dayNotes={dayNotes}
                    isEditing={isEditing}
                    isSavingThisDay={isSavingThisDay}
                    isDailySaving={isDailySaving}
                    editText={editText}
                    onChangeEditText={setEditText}
                    onSave={() => void handleSaveNote()}
                    onCancel={handleCancelEdit}
                    onStartEdit={() => handleStartEdit(dateKey)}
                    onDelete={() => void handleDeleteNote(dateKey)}
                  />
                )
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
